import React, { useState } from "react";
import {
    TickerBarWithPill,
    type DropdownOption,
} from "@bloomberg/media-ui-markets";
import "downshift";

import { useRealTimeData } from "~hooks/useRealTimeData";
import { useTickerData } from "~hooks/useTickerData";
import type { Security } from "~types/types";
import { truthyFilter } from "~utils/moduleIntegrity";
import {
    formatTickerBarSecurity,
    PHOENIX_TICKER_BARS,
} from "~utils/tickerBarUtils";

import styles from "./PhoenixTickerBarWrapper.module.css";

interface PhoenixTickerBarWrapperProps {
    tickerBar: Security[];
    pageId: string;
}

const SECURITIES = [
    { displayValue: "Top Securities", value: "top" },
    { displayValue: "Market Indices", value: "indices" },
    { displayValue: "Rates & Bonds", value: "rates" },
];

const TICKER_SET_OPTIONS = new Set(
    SECURITIES.map(({ value: option }) => option)
);

const [{ value: DEFAULT_TICKER_SET }] = SECURITIES;

const trackClick = (eventAction: string, eventLabel: string) =>
    window?.dataLayer?.push({
        event: "phoenix-tickerbar-click",
        eventCategory: "engagement",
        eventAction,
        eventLabel,
    });

const getRealTimeData = (tickerSecurities: Security[]): Security[] =>
    tickerSecurities.map((tickerSecurity) => {
        const { tickerData } = useTickerData({
            tickerId: tickerSecurity.id,
        });

        const realTimeData = useRealTimeData(
            tickerData?.realTimeTickerDescription
        );

        if (!realTimeData) {
            return tickerSecurity;
        }

        return { ...tickerSecurity, ...realTimeData };
    });

const PhoenixTickerBarWrapper: React.FC<PhoenixTickerBarWrapperProps> = ({
    tickerBar,
    pageId,
}) => {
    const [currentDropdownOption, setCurrentDropdownOption] =
        useState(DEFAULT_TICKER_SET);

    const tickerIds = new Set(
        (PHOENIX_TICKER_BARS[pageId] || PHOENIX_TICKER_BARS.default)[
            currentDropdownOption
        ]
    );

    const tickersDataMap = new Map(
        getRealTimeData(tickerBar).map((ticker) => [ticker.id, ticker])
    );

    // Preserve order of tickers
    const tickersData = Array.from(tickerIds)
        .filter((id) => tickersDataMap.has(id))
        .map((id) => tickersDataMap.get(id) as Security);

    const onSecuritiesUpdate = (dropdownOption?: DropdownOption | null) => {
        if (
            !dropdownOption?.value ||
            !TICKER_SET_OPTIONS.has(dropdownOption?.value)
        ) {
            return;
        }

        setCurrentDropdownOption(dropdownOption?.value);
    };

    return (
        <div className={styles.tickerBarWrapper}>
            <TickerBarWithPill
                displaySecurityName
                trackClick={trackClick}
                tickers={tickersData
                    .map((tickerData) => formatTickerBarSecurity(tickerData))
                    .filter(truthyFilter)}
                securities={SECURITIES}
                onSecuritiesUpdate={onSecuritiesUpdate}
            />
        </div>
    );
};

export default PhoenixTickerBarWrapper;
